import AuthService from './AuthService';
import PodService from './PodService';
import config from '../config';
import Cookies from 'js-cookie';
import { IPodUserRoleConfig } from '../models/PodUserRoleConfig';

import JwtReader from 'em-jwt-reader';

interface ISubscriptionResult {
    redirected: boolean;
    accessToken: IAccessTokenData;
    referAColleague: boolean;
    hasProRole: boolean;
    hasAISearchRole: boolean;
}

const redirectResult: ISubscriptionResult = { redirected: true, accessToken: null, referAColleague: false, hasProRole: false, hasAISearchRole: false};

class Subscriptions {
    private enablePodRedirection: boolean;
    private podUserRoleConfig?: IPodUserRoleConfig;

    public Init(enablePodRedirection: boolean, podUserRoleConfig?: IPodUserRoleConfig): ISubscriptionResult {
        const url = window.location.href.toLowerCase();

        const authenticateRedirectUrl = AuthService.Authenticate(document.cookie.indexOf('em_at=') >= 0, enablePodRedirection, config.pod);
        this.enablePodRedirection = enablePodRedirection;
        this.podUserRoleConfig = podUserRoleConfig;

        if (authenticateRedirectUrl) {
            window.location.href = authenticateRedirectUrl;
            return redirectResult;
        }

        // this call is a replacement for fetch branding/me, subscriptions/me, and subscriptions/authentication
        const accessToken = Cookies.get('em_at');
        const user = JwtReader.ReadAccessToken(accessToken);
        const referAColleague = JwtReader.IsInRole(accessToken, 'refer_a_colleague');
        const hasProRole = JwtReader.IsInRole(accessToken, 'ii_core');
        const hasAISearchRole = JwtReader.IsInRole(accessToken, 'em_ai_search');
        const usersPod = this.DetermineUsersPod(accessToken, user, this.podUserRoleConfig);
        const redirectUrl = PodService.DoPodRedirect(enablePodRedirection, user.authenticated, config.pod, url, usersPod);

        if (redirectUrl) {
            window.location.href = redirectUrl;
            return redirectResult;
        }

        if (user.authenticated) {
          this.InitTokenRefresh(user.expires, false);
        } else if (Cookies.get('em_rte')) {
          // if refresh token exists (rte)
          this.TokenRefreshInternal();
        }

        return { redirected: false, accessToken: user, referAColleague, hasProRole, hasAISearchRole };
    }

    private InitTokenRefresh(expTs: number, justIssued: boolean) {
        const currentTimeMs = new Date().getTime();
        const expTimeMs =  expTs * 1000;
        const remainingMs = expTimeMs - currentTimeMs;
        const maxTokenRemainingLifeInMs = config.maxTokenRemainingLifeInMinutes * 60 * 1000;

        if (remainingMs <= maxTokenRemainingLifeInMs && !justIssued) {
            this.TokenRefreshInternal();
            return;
        }

        const msUntilThirtyMinutesBeforeCutoff = remainingMs - maxTokenRemainingLifeInMs;

        if (msUntilThirtyMinutesBeforeCutoff >= 0) {
            setTimeout(this.TokenRefreshInternal.bind(this), msUntilThirtyMinutesBeforeCutoff);
        }
    }

    private TokenRefreshInternal() {
        fetch(`${config.subscriptionsServiceUrl}/RefreshToken`,
        {
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache',
            },
            method: 'POST',
        }).then((response) => {
            if (response.ok) {
                const url = window.location.href.toLowerCase();
                const accessToken = Cookies.get('em_at');
                const user = JwtReader.ReadAccessToken(accessToken);
                const usersPod = this.DetermineUsersPod(accessToken, user, this.podUserRoleConfig);
                const redirectUrl = PodService.DoPodRedirect(this.enablePodRedirection, user.authenticated, config.pod, url, usersPod);

                if (redirectUrl) {
                    window.location.href = redirectUrl;
                    return;
                }

                this.InitTokenRefresh(user.expires, true);
            }
        });
    }

    /**
     * Determines the user's pod based on their roles and PodUserRoleConfig.
     * PodUserRoleConfig is a parameter that is passed from the consuming service
     * @param accessToken The access token of the user.
     * @param user The user data.
     * @returns The pod assigned to the user.
     * Note: The config is only needed for Pro.EA and Freemium.EA services since they are served under content-na1 and content-naf domains.
     */
    public DetermineUsersPod(accessToken: string, user: IAccessTokenData, podUserRoleConfig?: IPodUserRoleConfig): string {

      if (podUserRoleConfig && user.authenticated) {
       // Sort podByUserRolesOrdered in ascending order by the 'order' property
        const sortedPodByUserRolesOrdered = podUserRoleConfig.podByUserRolesOrdered.sort((a, b) => a.order - b.order);

        for (const roleConfig of sortedPodByUserRolesOrdered) {
          const allRolesMatch = roleConfig.roles.every((role) => JwtReader.IsInRole(accessToken, role));
          // return the first matching item
          if (allRolesMatch) {
            return roleConfig.pod;
          }
        }
        return podUserRoleConfig.defaultPod;
      }

      return user.pod;
    }
}

export default new Subscriptions();
